/**
 * Importazione dello scadenziario dal foglio Excel del cliente.
 *
 * Il file si legge e si mostra: quante persone, quali corsi, quanti
 * attestati, cosa non si è capito. Si scrive solo dopo che qualcuno ha
 * guardato — perché un'importazione sbagliata su dati del personale è molto
 * più costosa da disfare che da evitare.
 */
import { useState } from 'react';
import { formazioneApi } from '../api';
import Modal from './Modal';

export default function ImportaScadenziario({ clienteId, nomeCliente, onClose, onFatto }) {
  const [file, setFile] = useState(null);
  const [letto, setLetto] = useState(null);
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');
  const [escluse, setEscluse] = useState(new Set());

  const analizza = async (f, foglio) => {
    if (!f) return;
    setLavorando(true); setErrore(''); if (!foglio) setLetto(null);
    try {
      setLetto(await formazioneApi.analizza(clienteId, f, foglio));
      setEscluse(new Set());
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  const importa = async () => {
    setLavorando(true); setErrore('');
    try {
      const r = await formazioneApi.importa(clienteId, {
        corsi: letto.corsi,
        persone: letto.persone.map(p => ({ ...p, salta: escluse.has(p.riga) })),
      });
      alert(`Importate ${r.nuove_persone} persone e ${r.nuovi_attestati} attestati.`
        + (r.saltati ? ` ${r.saltati} attestati già presenti o incompleti sono stati saltati.` : ''));
      onFatto?.();
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  const commuta = (riga) => setEscluse(s => {
    const n = new Set(s); n.has(riga) ? n.delete(riga) : n.add(riga); return n;
  });

  return (
    <Modal title={`Importa lo scadenziario — ${nomeCliente || ''}`} onClose={onClose} size="xl">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-gray-500">
          Il foglio a matrice che usate oggi: una riga per dipendente e due colonne per corso,
          data e scadenza. Si leggono anche i fogli a tre colonne, come quello delle idoneità
          mediche. Niente viene scritto finché non confermi.
        </p>

        <div>
          <label className="label">File Excel</label>
          <input type="file" accept=".xlsx,.xls" className="input py-1.5"
                 onChange={e => { const f = e.target.files?.[0]; setFile(f); analizza(f); }} />
        </div>

        {lavorando && !letto && <p className="text-xs text-gray-500">Leggo il foglio…</p>}
        {errore && (
          <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800">{errore}</div>
        )}

        {letto && (
          <>
            {letto.fogli.length > 1 && (
              <div>
                <label className="label">Foglio</label>
                <div className="flex gap-2 flex-wrap">
                  {letto.fogli.map(f => (
                    <button key={f} type="button"
                            className={`btn-sm btn ${f === letto.foglio ? 'bg-brand-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                            onClick={() => analizza(file, f)}>{f}</button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  I fogli si importano uno alla volta: finito questo, torna qui e scegli l'altro.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[['Persone', letto.conteggi.persone], ['Corsi', letto.conteggi.corsi],
                ['Attestati', letto.conteggi.attestati], ['Già in anagrafica', letto.gia_presenti]]
                .map(([t, n]) => (
                <div key={t} className="border rounded-lg px-3 py-2">
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">{t}</div>
                  <div className="text-lg font-semibold text-gray-800">{n}</div>
                </div>
              ))}
            </div>

            {letto.avvertenze?.length > 0 && (
              <div className="bg-gray-50 border rounded px-3 py-2 text-xs text-gray-600 space-y-0.5">
                {letto.avvertenze.map((a, i) => <div key={i}>· {a}</div>)}
              </div>
            )}

            {/* I corsi con la validità da indicare: senza, la scadenza non si
                calcola e resta solo quella scritta nel foglio. */}
            <div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Corsi riconosciuti
              </div>
              <div className="flex flex-wrap gap-1.5">
                {letto.corsi.map((c, i) => (
                  <span key={i} className={`text-xs px-2 py-1 rounded border ${c.mesi_validita
                    ? 'bg-white text-gray-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                    {c.nome}
                    <span className="ml-1 text-gray-400">
                      {c.mesi_validita ? `${c.mesi_validita} mesi` : 'validità da indicare'}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Persone
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <tbody>
                    {letto.persone.map(p => (
                      <tr key={p.riga} className={`border-b last:border-0 ${escluse.has(p.riga) ? 'opacity-40' : ''}`}>
                        <td className="px-3 py-1.5 w-8">
                          <input type="checkbox" className="w-4 h-4" checked={!escluse.has(p.riga)}
                                 onChange={() => commuta(p.riga)} />
                        </td>
                        <td className="px-3 py-1.5 font-medium">{p.nome}</td>
                        <td className="px-3 py-1.5 text-gray-500">{p.qualifica || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-500">{p.attestati.length} attestati</td>
                        <td className="px-3 py-1.5 text-right">
                          {p.esistente_id
                            ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                già presente{p.esistente_nome !== p.nome ? ` come “${p.esistente_nome}”` : ''}
                              </span>
                            : <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">nuova</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Chi è già in anagrafica non viene duplicato: si aggiungono solo gli attestati che
                mancano. Togli la spunta a chi non vuoi importare.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-secondary" onClick={onClose}>Annulla</button>
        <button className="btn-primary" disabled={!letto || lavorando} onClick={importa}>
          {lavorando ? 'Importo…' : 'Importa'}
        </button>
      </div>
    </Modal>
  );
}
